import dayjs from 'dayjs';
import { MEETING_REFERENCE_TYPES } from '../../../../constants/meetingReferenceTypes';
import { toLocalDay } from '../../../../utils/dateOnly';

// Get the reference type (handles both refType and referenceType)
export const getRefType = (ref) => ref.refType || ref.referenceType;

// Get type label for display
export const getRefTypeLabel = (ref) => {
  const type = getRefType(ref);
  if (type === MEETING_REFERENCE_TYPES.IDP_GOAL) {
    return 'Goal';
  }
  if (type === MEETING_REFERENCE_TYPES.IDP_ACTION) {
    return 'Action';
  }
  return 'Feedback Session';
};

// Get title for display
export const getRefTitle = (ref) => {
  const type = getRefType(ref);
  if (type === MEETING_REFERENCE_TYPES.PERFORMANCE_FEEDBACK_RESULT) {
    return ref.refEntityData?.feedbackSession?.name || ref.refEntityData?.skillsMatrix?.name || 'Feedback';
  }
  return ref.refEntityData?.title || ref.refEntityData?.name || 'Unknown';
};

// Get parent goal title for actions
export const getActionParentGoalTitle = (ref) => {
  const type = getRefType(ref);
  if (type === MEETING_REFERENCE_TYPES.IDP_ACTION) {
    return ref.refEntityData?.developmentGoal?.title || null;
  }
  return null;
};

// Check if reference is an action
export const isActionRef = (ref) => {
  return getRefType(ref) === MEETING_REFERENCE_TYPES.IDP_ACTION;
};

// Check if reference is a goal
export const isGoalRef = (ref) => {
  return getRefType(ref) === MEETING_REFERENCE_TYPES.IDP_GOAL;
};

// Get status parts for actions - returns { progress, isOverdue }
export const getRefStatusParts = (ref) => {
  const type = getRefType(ref);
  if (type === MEETING_REFERENCE_TYPES.IDP_ACTION) {
    const action = ref.refEntityData;
    if (action?.completedAt) {
      return { progress: null, isOverdue: false };
    }
    const progress = action?.currentProgress;
    const isOverdue = action?.deadlineAt && toLocalDay(action.deadlineAt)?.isBefore(dayjs(), 'day') && !action?.completedAt;
    return { progress, isOverdue };
  }
  return { progress: null, isOverdue: false };
};

// Get status text for actions (e.g., "(10%, overdue)")
export const getRefStatusText = (ref) => {
  const { progress, isOverdue } = getRefStatusParts(ref);
  let status = progress != null ? ` (${progress}%` : '';
  if (isOverdue) {
    status += status ? ', overdue)' : ' (overdue)';
  } else if (status) {
    status += ')';
  }
  return status;
};

// Check if reference is overdue
export const isRefOverdue = (ref) => {
  const data = ref.refEntityData;
  return data?.deadlineAt && toLocalDay(data.deadlineAt)?.isBefore(dayjs(), 'day') && !data?.completedAt;
};

// Check if reference is a feedback session
export const isFeedbackRef = (ref) => {
  return getRefType(ref) === MEETING_REFERENCE_TYPES.PERFORMANCE_FEEDBACK_RESULT;
};

// Get feedback session date text (e.g., "Session of 02/2026")
export const getFeedbackSessionDate = (ref) => {
  const session = ref.refEntityData?.feedbackSession;
  if (session?.month && session?.year) {
    return `Session of ${String(session.month).padStart(2, '0')}/${session.year}`;
  }
  return null;
};

// Get employee from reference (for feedback sessions)
export const getRefEmployee = (ref) => {
  return ref.refEntityData?.employee || null;
};

// Get meeting relation type from reference
export const getRefRelationType = (ref) => {
  return ref.meetingRelationType || null;
};

// Count references by type
export const countReferencesByType = (refs) => ({
  goals: refs.filter(r => getRefType(r) === MEETING_REFERENCE_TYPES.IDP_GOAL).length,
  actions: refs.filter(r => getRefType(r) === MEETING_REFERENCE_TYPES.IDP_ACTION).length,
  feedback: refs.filter(r => getRefType(r) === MEETING_REFERENCE_TYPES.PERFORMANCE_FEEDBACK_RESULT).length,
});

// Build summary text like "1 goal · 2 actions · 1 feedback"
// includeLeadingSeparator: if true, prepends " · " when there are items (default: true)
export const buildLinkedItemsSummary = (refs, includeLeadingSeparator = true) => {
  const counts = countReferencesByType(refs);
  const parts = [];
  if (counts.goals > 0) {
    parts.push(`${counts.goals} goal${counts.goals !== 1 ? 's' : ''}`);
  }
  if (counts.actions > 0) {
    parts.push(`${counts.actions} action${counts.actions !== 1 ? 's' : ''}`);
  }
  if (counts.feedback > 0) {
    parts.push(`${counts.feedback} feedback`);
  }
  if (parts.length === 0) {
    return '';
  }
  const joined = parts.join(' · ');
  return includeLeadingSeparator ? ' · ' + joined : joined;
};
